import { type CSSProperties, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';

import { advancedFlowViewStore } from '../store/AdvancedFlowViewStore';

import { useCurrentProps } from '../../../../hooks/useCurrentProps';

import { Connector } from './Connector';
import type { ConnectorProps } from './Connector';
import { connectorTypesMap } from './connectorTypes';
import { useConnectorRegister } from './useConnectorRegister';

export type ExecutionState = { type: string; [key: string]: unknown };

type NodeChild = { id: string };

type NodeParent = { id: string; fromConnectorType: string; isPlaceholder?: boolean };

export interface ConnectorInProps extends Omit<
  ConnectorProps,
  'dragHandleRef' | 'isConnectorDone' | 'isSiblingActive' | 'isDragging' | 'children'
> {
  nodeId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cardRootRef: any; // forwarded to react-dnd drop target; can be any ref-like
  activeConnectors: Set<string>;
  executionState: ExecutionState;
  style?: CSSProperties;
  isHovered?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isDisabledStyle?: string;
  isVisibleOverride?: boolean;
  isDragOverAndCanConnect?: boolean;

  nodeChildren: Record<string, NodeChild[] | undefined>;
  nodeParents: Record<string, NodeParent[] | undefined>;

  onIsDraggingAndCanConnectChange: (value: boolean) => void;
  onIsDragOverAndCanConnectChange: (value: boolean) => void;
}

export function ConnectorIn(props: ConnectorInProps) {
  const currentProps = useCurrentProps({
    onIsDraggingAndCanConnectChange: props.onIsDraggingAndCanConnectChange,
    onIsDragOverAndCanConnectChange: props.onIsDragOverAndCanConnectChange,
  });

  const circleRef = useRef<SVGCircleElement | null>(null);

  const [collectedProps, connectorInDropTarget] = useDrop(() => {
    const accept = [
      connectorTypesMap.Out,
      connectorTypesMap.Error,
      connectorTypesMap.True,
      connectorTypesMap.False,
    ];

    return {
      accept: accept,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      collect: (dropMonitor: any) => {
        return {
          isDragOver: dropMonitor.isOver({ shallow: true }),
          canDrop: dropMonitor.canDrop(),
          // This method seems to be called on any drag operation so it can receive
          // flowcard items even when they are not in accept. So check the accept array for
          // the current item type and dont return the item if we do not care about it.
          //
          // This seems like a bug because this component renders/collects for no reason now.
          item: accept.includes(dropMonitor.getItemType()) ? dropMonitor.getItem() : null,
        };
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      canDrop(dragItem: any, _dropMonitor: any) {
        if (dragItem.nodeId === props.nodeId || props.isDisabled) {
          return false;
        }

        function isDragItemChild(children?: NodeChild[] | null): boolean {
          if (children == null) {
            return false;
          }

          if (children.some((child) => child.id === dragItem.nodeId)) {
            return true;
          }

          for (const child of children) {
            const childChildren = props.nodeChildren[child.id];
            const result = isDragItemChild(childChildren ?? null);

            if (result === true) {
              return result;
            }
          }

          return false;
        }

        const children = props.nodeChildren[props.nodeId];
        const result = isDragItemChild(children ?? null);

        return result !== true;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      hover(_item: any, _monitor: any) {},
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      drop(dragItem: any, monitor: any) {
        if (monitor.didDrop()) return;

        const fromNodeId = dragItem.nodeId;

        advancedFlowViewStore.createConnection({
          fromNodeId: fromNodeId,
          fromConnectorType: monitor.getItemType(),
          toNodeId: props.nodeId,
          toConnectorType: connectorTypesMap.In,
        });
      },
    };
  }, [props.nodeId, props.nodeChildren, props.isDisabled]);

  // We attach the behavior to the whole card.
  connectorInDropTarget(props.cardRootRef);

  useEffect(() => {
    return function () {
      connectorInDropTarget(null);
    };
  }, [connectorInDropTarget]);

  useConnectorRegister({
    cardRootRef: props.cardRootRef,
    centerRef: circleRef,
    type: connectorTypesMap.In,
    nodeId: props.nodeId,
    centerRefs: null,
  });

  const isActive = props.activeConnectors.has(`${props.nodeId}::${connectorTypesMap.In}`);
  const isDraggingAndCanConnect = collectedProps.item != null && collectedProps.canDrop === true;
  const isDragOverAndCanConnect =
    collectedProps.item != null &&
    collectedProps.canDrop === true &&
    collectedProps.isDragOver === true;

  useEffect(() => {
    currentProps.onIsDraggingAndCanConnectChange(isDraggingAndCanConnect);
  }, [currentProps, isDraggingAndCanConnect]);

  useEffect(() => {
    currentProps.onIsDragOverAndCanConnectChange(isDragOverAndCanConnect);
  }, [currentProps, isDragOverAndCanConnect]);

  let connectorTypeColorOverride: string | null = null;

  const thisNodeParents = props.nodeParents[props.nodeId];

  // Get the first connected parent node so we can show a connected color for the circle.
  if (thisNodeParents != null) {
    const thisNodeFirstParent = thisNodeParents[0];
    connectorTypeColorOverride = thisNodeFirstParent.fromConnectorType;
  }

  return (
    <Connector
      circleRef={circleRef}
      positionStyle={props.style}
      connectorType={connectorTypesMap.In}
      connectorTypeColorOverride={connectorTypeColorOverride}
      executionState={props.executionState}
      isActive={isActive}
      isDraggingAndCanConnect={isDraggingAndCanConnect}
      isDragOverAndCanConnect={isDragOverAndCanConnect}
      isHovered={props.isHovered}
      isSelected={props.isSelected}
      isDisabled={props.isDisabled}
      isDisabledStyle={props.isDisabledStyle}
      isVisibleOverride={props.isVisibleOverride}
      direction={props.direction}
    />
  );
}
